import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import { TrustBanner, TrustBadges } from '../components/TrustSignals';
import { StripeCheckoutModal } from '../components/StripeCheckout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Search, MapPin, Clock, Star, Wrench, Package, Plus, Minus, 
  ShoppingCart, Truck, ArrowLeft, Loader2, Phone, X, Check, 
  Filter, Store, DollarSign, AlertCircle, Car, Settings, Zap
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  'Motor Oil': Zap,
  'Batteries': Zap,
  'Brakes': Settings,
  'Filters': Filter,
  'Lighting': Zap,
  'Tools': Wrench,
  'Fluids': Package,
  'Accessories': Star,
};

// Auto Parts Marketplace Page
export default function AutoPartsPage() {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStore, setSelectedStore] = useState('all');
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryPhone, setDeliveryPhone] = useState('');
  const [processingOrder, setProcessingOrder] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [productsRes, storesRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/auto-parts/ads?limit=100`),
        axios.get(`${API}/auto-parts/stores`),
        axios.get(`${API}/auto-parts/categories`)
      ]);
      
      setProducts(productsRes.data.ads || []);
      setStores(storesRes.data.stores || []);
      setCategories(categoriesRes.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch auto parts data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.brand?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    const matchesStore = selectedStore === 'all' || product.store_id === selectedStore;
    return matchesSearch && matchesCategory && matchesStore;
  });

  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.ad_id === product.ad_id);
      if (existing) {
        return prev.map(item =>
          item.ad_id === product.ad_id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
    toast.success(`Added ${product.product_name} to cart`);
  };

  const updateCartQuantity = (adId, delta) => {
    setCart(prev => {
      const updated = prev.map(item => {
        if (item.ad_id === adId) {
          const newQty = item.quantity + delta;
          return newQty > 0 ? { ...item, quantity: newQty } : item;
        }
        return item;
      }).filter(item => item.quantity > 0);
      return updated;
    });
  };

  const removeFromCart = (adId) => {
    setCart(prev => prev.filter(item => item.ad_id !== adId));
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.customer_price * item.quantity), 0);
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const handlePlaceOrder = async () => {
    if (!user) {
      toast.error('Please login to place an order');
      navigate('/login');
      return;
    }

    if (!deliveryAddress.trim()) {
      toast.error('Please enter a delivery address');
      return;
    }

    setProcessingOrder(true);
    try {
      const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
      
      // Create order for each item (they may be from different stores)
      for (const item of cart) {
        await axios.post(
          `${API}/auto-parts/order`,
          {
            ad_id: item.ad_id,
            quantity: item.quantity,
            delivery_address: deliveryAddress,
            delivery_phone: deliveryPhone
          },
          { headers, withCredentials: true }
        );
      }
      
      toast.success('Order placed! You will be charged AFTER admin approval.');
      setCart([]);
      setShowCheckout(false);
      setDeliveryAddress('');
      setDeliveryPhone('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to place order');
    } finally {
      setProcessingOrder(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="card-glass p-8 text-center">
            <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading auto parts...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50" data-testid="auto-parts-page">
      <Header />
      
      {/* Modern Hero Section */}
      <div className="hero-gradient-parts relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10" />
        <div className="absolute inset-0 noise-overlay opacity-10" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <div className="animate-fade-in-up">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Zap className="w-4 h-4 text-yellow-300" />
              <span className="text-white text-sm font-medium">Same-Day Pickup & Delivery</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 drop-shadow-lg">
              Auto Parts <span className="text-cyan-300">Delivered</span>
            </h1>
            <p className="text-white/90 text-lg md:text-xl max-w-2xl mx-auto mb-6">
              We pick up from AutoZone, O'Reilly, NAPA & more — and deliver straight to you!
            </p>
            
            {/* Payment notice */}
            <div className="inline-flex items-center gap-2 bg-yellow-400/20 backdrop-blur-sm px-6 py-3 rounded-full">
              <AlertCircle className="w-5 h-5 text-yellow-300" />
              <span className="text-white font-medium">Your card is NOT charged until admin approval</span>
            </div>
          </div>
        </div>
        
        {/* Wave decoration */}
        <svg className="absolute bottom-0 left-0 w-full" viewBox="0 0 1440 100" fill="none">
          <path d="M0 100V50C240 16 480 0 720 0C960 0 1200 16 1440 50V100H0Z" fill="white" fillOpacity="0.1"/>
          <path d="M0 100V60C240 26 480 10 720 10C960 10 1200 26 1440 60V100H0Z" className="fill-blue-50"/>
        </svg>
      </div>
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 -mt-4">
        {/* Modern Search & Filters */}
        <div className="card-glass p-6 mb-8 animate-fade-in-up">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
              <input
                type="text"
                data-testid="auto-parts-search"
                placeholder="Search parts, brands, stores..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-modern w-full"
              />
            </div>
            
            <div className="flex flex-wrap gap-3">
              {/* Category chips */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedCategory('all')}
                  className={`chip-modern ${selectedCategory === 'all' ? 'active' : ''}`}
                >
                  All Parts
                </button>
                {categories.slice(0, 4).map(cat => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`chip-modern ${selectedCategory === cat ? 'active' : ''}`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
              
              {/* Cart Button */}
              <Button
                data-testid="cart-button"
                onClick={() => setShowCart(true)}
                className="relative bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-6 rounded-full"
              >
                <ShoppingCart className="h-5 w-5 mr-2" />
                Cart
                {cartItemCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-6 w-6 flex items-center justify-center cart-badge-pulse">
                    {cartItemCount}
                  </span>
                )}
              </Button>
            </div>
          </div>
          
          {/* Store filter */}
          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-100">
            <span className="text-sm text-gray-500 mr-2 self-center">Store:</span>
            <button
              onClick={() => setSelectedStore('all')}
              className={`text-sm px-3 py-1 rounded-full transition ${selectedStore === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              All Stores
            </button>
            {stores.map(store => (
              <button
                key={store.store_id}
                onClick={() => setSelectedStore(store.store_id)}
                className={`text-sm px-3 py-1 rounded-full transition flex items-center gap-2 ${selectedStore === store.store_id ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {store.logo && <img src={store.logo} alt="" className="w-4 h-4 object-contain" />}
                {store.name}
              </button>
            ))}
          </div>
        </div>

        {/* Products Grid */}
        {filteredProducts.length === 0 ? (
          <div className="card-glass p-16 text-center animate-fade-in-up">
            <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-full flex items-center justify-center">
              <Package className="h-12 w-12 text-blue-500" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">No Products Found</h3>
            <p className="text-gray-500 max-w-md mx-auto">
              {searchTerm || selectedCategory !== 'all' || selectedStore !== 'all'
                ? `No results for your filters. Try adjusting your search.`
                : 'Check back soon for available auto parts!'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProducts.map((product, index) => {
              const Icon = CATEGORY_ICONS[product.category] || Wrench;
              const inCart = cart.find(item => item.ad_id === product.ad_id);
              
              return (
                <div 
                  key={product.ad_id} 
                  className="card-modern group animate-fade-in-up"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`product-card-${product.ad_id}`}
                >
                  {/* Product Image */}
                  <div className="product-image-modern h-48">
                    {product.image ? (
                      <img 
                        src={product.image} 
                        alt={product.product_name}
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-blue-100 to-cyan-100 flex items-center justify-center">
                        <Icon className="h-16 w-16 text-blue-400" />
                      </div>
                    )}
                    {/* Stock badge */}
                    {product.in_stock === false && (
                      <div className="absolute top-3 left-3 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                        Out of Stock
                      </div>
                    )}
                    {/* Store badge */}
                    <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-lg text-xs font-medium text-gray-700 flex items-center gap-1">
                      <Store className="h-3 w-3" />
                      {product.store_name?.split(' ')[0]}
                    </div>
                  </div>
                  
                  <div className="p-5">
                    <h3 className="font-semibold text-gray-900 line-clamp-2 mb-2 group-hover:text-blue-600 transition-colors">
                      {product.product_name}
                    </h3>
                    
                    <div className="flex items-center gap-2 mb-3">
                      <span className="chip-modern text-xs py-1 px-2">{product.category}</span>
                      <span className="text-xs text-gray-500">{product.brand}</span>
                    </div>
                    
                    <div className="flex items-center justify-between mb-4">
                      <div className="price-modern">
                        <span className="text-xs text-gray-400 line-through mr-2">
                          {formatCurrency(product.store_price)}
                        </span>
                        <span className="currency">$</span>
                        <span className="amount">{Math.floor(product.customer_price)}</span>
                        <span className="cents">.{((product.customer_price % 1) * 100).toFixed(0).padStart(2, '0')}</span>
                      </div>
                    </div>
                    
                    {inCart ? (
                      <div className="qty-selector-modern w-full justify-between">
                        <button onClick={() => updateCartQuantity(product.ad_id, -1)}>
                          <Minus className="h-4 w-4" />
                        </button>
                        <span className="qty-value">{inCart.quantity}</span>
                        <button onClick={() => updateCartQuantity(product.ad_id, 1)}>
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <Button
                        data-testid={`add-to-cart-${product.ad_id}`}
                        onClick={() => addToCart(product)}
                        disabled={product.in_stock === false}
                        className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white rounded-xl gap-2 btn-press"
                      >
                        <Plus className="h-4 w-4" />
                        Add to Cart
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Cart Sidebar */}
        {showCart && (
          <div className="fixed inset-0 z-50 flex justify-end" data-testid="cart-sidebar">
            <div 
              className="absolute inset-0 bg-black/50"
              onClick={() => setShowCart(false)}
            />
            <div className="relative w-full max-w-md bg-white shadow-xl flex flex-col h-full">
              <div className="p-4 border-b flex items-center justify-between">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5" />
                  Your Cart ({cartItemCount})
                </h2>
                <Button variant="ghost" size="sm" onClick={() => setShowCart(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4">
                {cart.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <ShoppingCart className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>Your cart is empty</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {cart.map((item) => (
                      <div key={item.ad_id} className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center shrink-0">
                          {item.image ? (
                            <img src={item.image} alt={item.product_name} className="w-full h-full object-cover rounded" />
                          ) : (
                            <Wrench className="h-8 w-8 text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm line-clamp-2">{item.product_name}</p>
                          <p className="text-xs text-gray-500">{item.store_name}</p>
                          <div className="flex items-center justify-between mt-2">
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateCartQuantity(item.ad_id, -1)}
                                className="h-6 w-6 p-0"
                              >
                                <Minus className="h-3 w-3" />
                              </Button>
                              <span className="text-sm font-medium">{item.quantity}</span>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateCartQuantity(item.ad_id, 1)}
                                className="h-6 w-6 p-0"
                              >
                                <Plus className="h-3 w-3" />
                              </Button>
                            </div>
                            <span className="font-bold text-green-600">
                              {formatCurrency(item.customer_price * item.quantity)}
                            </span>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFromCart(item.ad_id)}
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {cart.length > 0 && (
                <div className="border-t p-4 space-y-4">
                  <div className="flex items-center justify-between text-lg font-bold">
                    <span>Total</span>
                    <span className="text-green-600">{formatCurrency(cartTotal)}</span>
                  </div>
                  
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-xs text-yellow-800">
                      <AlertCircle className="inline h-3 w-3 mr-1" />
                      Payment will be processed AFTER admin approves your order.
                    </p>
                  </div>
                  
                  <Button
                    data-testid="proceed-to-checkout"
                    onClick={() => {
                      setShowCart(false);
                      setShowCheckout(true);
                    }}
                    className="w-full bg-green-600 hover:bg-green-700 gap-2"
                  >
                    <Truck className="h-5 w-5" />
                    Proceed to Checkout
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Checkout Modal */}
        {showCheckout && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="checkout-modal">
            <div 
              className="absolute inset-0 bg-black/50"
              onClick={() => setShowCheckout(false)}
            />
            <Card className="relative w-full max-w-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Truck className="h-5 w-5" />
                  Delivery Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Delivery Address *</label>
                  <Input
                    data-testid="delivery-address"
                    placeholder="Enter your full delivery address"
                    value={deliveryAddress}
                    onChange={(e) => setDeliveryAddress(e.target.value)}
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-1 block">Phone Number</label>
                  <Input
                    data-testid="delivery-phone"
                    placeholder="Phone for delivery updates"
                    value={deliveryPhone}
                    onChange={(e) => setDeliveryPhone(e.target.value)}
                  />
                </div>
                
                {/* Order Summary */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium mb-2">Order Summary</h4>
                  {cart.map(item => (
                    <div key={item.ad_id} className="flex justify-between text-sm py-1">
                      <span>{item.product_name} x{item.quantity}</span>
                      <span>{formatCurrency(item.customer_price * item.quantity)}</span>
                    </div>
                  ))}
                  <div className="border-t mt-2 pt-2 flex justify-between font-bold">
                    <span>Total</span>
                    <span className="text-green-600">{formatCurrency(cartTotal)}</span>
                  </div>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <AlertCircle className="inline h-4 w-4 mr-1" />
                    <strong>How it works:</strong> After you submit, our team will verify product availability. 
                    Once approved, your card will be charged and a driver will pick up your parts and deliver them to you!
                  </p>
                </div>
                
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowCheckout(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    data-testid="place-order-btn"
                    onClick={handlePlaceOrder}
                    disabled={processingOrder || !deliveryAddress.trim()}
                    className="flex-1 bg-green-600 hover:bg-green-700 gap-2"
                  >
                    {processingOrder ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Check className="h-4 w-4" />
                        Place Order
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
